import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  CONTACT_PROFILE_LEAD_SUGGESTIONS,
  contactProfileField,
  isContactProfileFieldKey,
  type ContactProfileValues,
  type UpdateContactBody,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Eyebrow } from '../../../ds/components';
import type { PropertyGroup, PropertyRow } from '../types';
import styles from './record.module.css';

export type PropertyAction = 'lifecycle' | 'dnc' | 'owner' | 'referrer';

export interface PropertiesCardProps {
  groups: PropertyGroup[];
  editNote: string;
  editMode: boolean;
  onToggleEdit: () => void;
  /** „Alle Eigenschaften": the dialog with every property and its versions. */
  onOpenAll: () => void;
  /** Inline field saved (name, phone, e-mail, address, opt-ins) → `PATCH /contacts/:id`. */
  onSave: (body: UpdateContactBody) => Promise<void>;
  /** „Lead-Angaben" row saved (D-341) → `PATCH /contacts/:id/profile` like „Alle Eigenschaften". */
  onSaveProfile: (body: ContactProfileValues) => Promise<void>;
  /** Rows that need a reason open their dialog. */
  onAction: (action: PropertyAction) => void;
  error: string | null;
}

const INLINE_KEYS: ReadonlySet<string> = new Set([
  'name',
  'phone',
  'email',
  'address',
  'gdprOptIn',
  'whatsappOptIn',
]);
/** „Lead-Angaben" rows edited inline through the profile (D-341); `leadAnswers` stays read-only. */
const PROFILE_KEYS: ReadonlySet<string> = new Set([
  'employment',
  'leadIncomeBand',
  'leadEquityAvailability',
  'leadEquityBand',
  'reachability',
]);
/** Accessible names of the rows that open a dialog (the dialogs' own titles). */
const DIALOG_LABELS: Record<string, string> = {
  lifecycle: 'Lifecycle ändern',
  dnc: 'DNC setzen',
  owner: 'Besitzer ändern',
  referrer: 'Tippgeber ändern',
};

const DIALOG_KEYS: Record<string, PropertyAction> = {
  lifecycle: 'lifecycle',
  dnc: 'dnc',
  owner: 'owner',
  referrer: 'referrer',
};

/** A typed value as the PATCH body: name splits at the last space, the address at the comma. */
function patchFor(key: string, text: string): UpdateContactBody | null {
  const value = text.trim();
  switch (key) {
    case 'name': {
      const index = value.lastIndexOf(' ');
      if (index <= 0) return { lastName: value };
      return { firstName: value.slice(0, index), lastName: value.slice(index + 1) };
    }
    case 'phone':
      return value ? { phones: [{ number: value, isPrimary: true }] } : { phones: [] };
    case 'email':
      return value ? { emails: [{ email: value, isPrimary: true }] } : { emails: [] };
    case 'address': {
      if (!value) return { street: null, postalCode: null, city: null };
      const comma = value.indexOf(',');
      if (comma < 0) return { city: value };
      const street = value.slice(0, comma).trim();
      const rest = value.slice(comma + 1).trim();
      const match = /^(\d{4,5})\s+(.+)$/.exec(rest);
      return match
        ? { street, postalCode: match[1] ?? null, city: match[2] ?? null }
        : { street, city: rest };
    }
    default:
      return null;
  }
}

const TONE_CLASS: Partial<Record<string, string | undefined>> = {
  danger: styles.propValueDanger,
  accent: styles.propValueAccent,
  info: styles.propValueInfo,
  neutral: styles.propValueMuted,
};

function toneClass(row: PropertyRow): string | undefined {
  if (row.value === null) return styles.propValueMuted;
  return row.tone ? TONE_CLASS[row.tone] : undefined;
}

function InlineText({
  initial,
  suggestions,
  onCommit,
  onCancel,
}: {
  initial: string;
  /** Offered values (a datalist); any other text is allowed. */
  suggestions?: readonly string[] | undefined;
  onCommit: (text: string) => void;
  onCancel: () => void;
}) {
  const listId = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(initial);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      onCommit(text);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  }
  return (
    <>
      <input
        ref={ref}
        className={styles.propInput}
        value={text}
        list={suggestions ? listId : undefined}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => onCommit(text)}
      />
      {suggestions ? (
        <datalist id={listId}>
          {suggestions.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}

/** The enum rows of „Lead-Angaben" (Beschäftigung): the catalog options, „—" clears. */
function InlineOption({
  options,
  initial,
  onCommit,
}: {
  options: readonly { value: string; label: string }[];
  initial: string;
  onCommit: (value: string) => void;
}) {
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <select
      ref={ref}
      className={styles.propInput}
      defaultValue={initial}
      onChange={(event) => onCommit(event.target.value)}
      onBlur={(event) => onCommit(event.target.value)}
    >
      <option value="">—</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** The stored value of a profile row: enum labels back to their identifier. */
function profileInitial(row: PropertyRow): string {
  if (row.value === null || !isContactProfileFieldKey(row.key)) return '';
  const field = contactProfileField(row.key);
  return field.options?.find((option) => option.label === row.value)?.value ?? row.value;
}

function InlineOptIn({ granted, onCommit }: { granted: boolean; onCommit: (value: boolean) => void }) {
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <select
      ref={ref}
      className={styles.propInput}
      defaultValue={granted ? 'yes' : 'no'}
      onChange={(event) => onCommit(event.target.value === 'yes')}
      onBlur={(event) => onCommit(event.target.value === 'yes')}
    >
      <option value="yes">erteilt</option>
      <option value="no">nicht erteilt</option>
    </select>
  );
}

/** The record's „Eigenschaften" card (I.01–I.11) with the edit mode of I.07. */
export function PropertiesCard({
  groups,
  editNote,
  editMode,
  onToggleEdit,
  onOpenAll,
  onSave,
  onSaveProfile,
  onAction,
  error,
}: PropertiesCardProps) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);

  async function commit(row: PropertyRow, text: string) {
    setEditing(null);
    const body = patchFor(row.key, text);
    if (!body || text.trim() === (row.value ?? '').trim()) return;
    await onSave(body);
  }

  async function commitProfile(row: PropertyRow, text: string) {
    setEditing(null);
    if (!isContactProfileFieldKey(row.key)) return;
    const value = text.trim();
    if (value === profileInitial(row)) return;
    await onSaveProfile({ [row.key]: value === '' ? null : value });
  }

  async function commitOptIn(row: PropertyRow, granted: boolean) {
    setEditing(null);
    const current = row.value !== null && !/^nicht erteilt|widerrufen/.test(row.value);
    if (granted === current) return;
    await onSave(row.key === 'gdprOptIn' ? { gdprOptIn: granted } : { whatsappOptIn: granted });
  }

  function rowKind(row: PropertyRow): 'inline' | 'dialog' | 'fixed' {
    if (row.key in DIALOG_KEYS && (row.key !== 'dnc' || (row.value ?? '').startsWith('nein')))
      return 'dialog';
    if (row.editable && (INLINE_KEYS.has(row.key) || PROFILE_KEYS.has(row.key))) return 'inline';
    return 'fixed';
  }

  return (
    <section className={styles.card} aria-label="Eigenschaften">
      <div className={clsx(styles.cardHead, styles.cardHeadCenter, styles.cardHeadWrap)}>
        <button
          type="button"
          className={styles.eyebrowButton}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <Eyebrow size={11}>Eigenschaften</Eyebrow>
        </button>
        <span className={styles.cardHeadActions}>
          <Button variant="ghost" size="small" onClick={onOpenAll}>
            Alle Eigenschaften
          </Button>
          <Button variant={editMode ? 'primary' : 'ghost'} size="small" onClick={onToggleEdit}>
            {editMode ? 'Fertig' : 'Bearbeiten'}
          </Button>
          <button
            type="button"
            className={styles.cardCaret}
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? '–' : '+'}
          </button>
        </span>
      </div>
      {editMode ? <p className={styles.editNote}>{editNote}</p> : null}
      {editMode && error ? (
        <div style={{ marginTop: 10 }}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}
      {open
        ? groups.map((group) => (
            <div key={group.key} className={styles.propGroup}>
              <div className={styles.propGroupLabel}>{group.label}</div>
              <div className={styles.propRows}>
                {group.rows.map((row) => {
                  const kind = editMode ? rowKind(row) : 'fixed';
                  const isEditing = editing === row.key;
                  const text = row.value ?? '—';
                  return (
                    <div key={row.key} className={styles.propRow}>
                      <span className={styles.propLabel}>{row.label}</span>
                      {isEditing && (row.key === 'gdprOptIn' || row.key === 'whatsappOptIn') ? (
                        <InlineOptIn
                          granted={row.value !== null && !/^nicht erteilt|widerrufen/.test(row.value)}
                          onCommit={(value) => void commitOptIn(row, value)}
                        />
                      ) : isEditing && PROFILE_KEYS.has(row.key) && isContactProfileFieldKey(row.key) ? (
                        contactProfileField(row.key).options ? (
                          <InlineOption
                            options={contactProfileField(row.key).options ?? []}
                            initial={profileInitial(row)}
                            onCommit={(value) => void commitProfile(row, value)}
                          />
                        ) : (
                          <InlineText
                            initial={profileInitial(row)}
                            suggestions={CONTACT_PROFILE_LEAD_SUGGESTIONS[row.key]}
                            onCommit={(value) => void commitProfile(row, value)}
                            onCancel={() => setEditing(null)}
                          />
                        )
                      ) : isEditing ? (
                        <InlineText
                          initial={row.value ?? ''}
                          onCommit={(value) => void commit(row, value)}
                          onCancel={() => setEditing(null)}
                        />
                      ) : kind === 'fixed' ? (
                        <span
                          className={clsx(
                            styles.propValue,
                            editMode && styles.propValueEditing,
                            toneClass(row),
                          )}
                        >
                          {text}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={clsx(
                            styles.propValue,
                            styles.propValueEditing,
                            styles.propValueEditable,
                            toneClass(row),
                          )}
                          aria-label={
                            kind === 'dialog'
                              ? (DIALOG_LABELS[row.key] ?? row.label)
                              : `${row.label} bearbeiten`
                          }
                          onClick={() => {
                            if (kind === 'dialog') onAction(DIALOG_KEYS[row.key] ?? 'owner');
                            else setEditing(row.key);
                          }}
                        >
                          {text}
                        </button>
                      )}
                      {editMode ? (
                        <span
                          className={clsx(styles.propPen, kind === 'fixed' && styles.propPenFixed)}
                          aria-hidden="true"
                        >
                          {kind === 'fixed' ? 'fix' : '✎'}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        : null}
    </section>
  );
}
